import { useContext, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { AuthContext } from "../context/AuthContext";
import "../styles/AdminTools.css";

const DAYS = [
  { v: 1, label: "Mon" },
  { v: 2, label: "Tue" },
  { v: 3, label: "Wed" },
  { v: 4, label: "Thu" },
  { v: 5, label: "Fri" },
  { v: 6, label: "Sat" },
  { v: 0, label: "Sun" },
];

export default function StudentPanel({ onBackClick }) {
  const { userData, authLoading } = useContext(AuthContext);

  const role = userData?.role || "user";
  const canManageStudents = role === "admin"; // keep admin-only for now

  const [courses, setCourses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [students, setStudents] = useState([]);

  const [loading, setLoading] = useState(false);

  // ---- Create Course ----
  const [courseName, setCourseName] = useState("");
  const [courseCode, setCourseCode] = useState("");

  // ---- Create Term ----
  const [termName, setTermName] = useState("");
  const [termStart, setTermStart] = useState("");
  const [termEnd, setTermEnd] = useState("");

  // ---- Selectors ----
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedTermId, setSelectedTermId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");

  // ---- Assign student courses ----
  const selectedStudent = useMemo(
    () => students.find((s) => s.id === selectedStudentId),
    [students, selectedStudentId]
  );

  const [studentCourseIds, setStudentCourseIds] = useState([]);

  // ---- Timetable form ----
  const [ttDay, setTtDay] = useState(1);
  const [ttStart, setTtStart] = useState("09:00");
  const [ttEnd, setTtEnd] = useState("11:00");
  const [ttRoom, setTtRoom] = useState("");
  const [ttCampus, setTtCampus] = useState("");
  const [ttLecturer, setTtLecturer] = useState("");

  const [timetableRows, setTimetableRows] = useState([]);

  // ---- Units form ----
  const [unitLines, setUnitLines] = useState("");
  const [savingUnits, setSavingUnits] = useState(false);
  const [existingUnitsDocId, setExistingUnitsDocId] = useState(null);
  const [unitTitle, setUnitTitle] = useState("");
  const [unitStart, setUnitStart] = useState("");
  const [unitEnd, setUnitEnd] = useState("");
  const [termUnits, setTermUnits] = useState([]); // array of unit objects


    // 1) Load courses/terms/students once auth is ready (and admin)
    useEffect(() => {
    if (authLoading) return;
    if (!canManageStudents) return;

    loadAll().catch(console.error);
    }, [authLoading, canManageStudents]);

    // 2) Load timetable + units whenever term/course changes (and admin)
    useEffect(() => {
    if (authLoading) return;
    if (!canManageStudents) return;

    if (!selectedTermId || !selectedCourseId) {
        setTimetableRows([]);
        setExistingUnitsDocId(null);
        setUnitLines("");
        return;
    }

    // load both in parallel
    Promise.all([loadTimetable(), loadUnits()]).catch(console.error);
    }, [authLoading, canManageStudents, selectedTermId, selectedCourseId]);

    // keep local studentCourseIds in sync when picking student
    useEffect(() => {
    setStudentCourseIds(selectedStudent?.courseIds || []);
    }, [selectedStudent]);



  const loadAll = async () => {
    setLoading(true);
    try {
      const [cSnap, tSnap, uSnap] = await Promise.all([
        getDocs(collection(db, "courses")),
        getDocs(collection(db, "terms")),
        getDocs(collection(db, "users")), // assumes admins can read users
      ]);

      setCourses(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTerms(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const allUsers = uSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setStudents(allUsers.filter((u) => u.role === "student"));
    } finally {
      setLoading(false);
    }
  };



  // ---------- Guards ----------
  if (authLoading) {
    return (
      <div className="content-area">
        <div className="admin-muted">Loading…</div>
      </div>
    );
  }

  if (!canManageStudents) {
    return (
      <div className="content-area">
        <div className="page-header">
          <h1 className="page-title">Student Panel</h1>
          <p className="page-subtitle">You don’t have permission to access this page.</p>
          <button className="btn btn-secondary" type="button" onClick={onBackClick}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // ---------- Actions ----------
  const createCourse = async () => {
    if (!courseName.trim()) return alert("Enter course name");
    await addDoc(collection(db, "courses"), {
      name: courseName.trim(),
      code: courseCode.trim(),
      active: true,
      createdAt: serverTimestamp(),
    });
    setCourseName("");
    setCourseCode("");
    await loadAll();
  };

  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    await deleteDoc(doc(db, "courses", id));
    await loadAll();
  };

  const createTerm = async () => {
    if (!termName.trim()) return alert("Enter term name");
    await addDoc(collection(db, "terms"), {
      name: termName.trim(),
      startDate: termStart || null,
      endDate: termEnd || null,
      active: true,
      createdAt: serverTimestamp(),
    });
    setTermName("");
    setTermStart("");
    setTermEnd("");
    await loadAll();
  };

  const deleteTerm = async (id) => {
    if (!window.confirm("Delete this term?")) return;
    await deleteDoc(doc(db, "terms", id));
    await loadAll();
  };

  const toggleStudentCourse = (courseId) => {
    setStudentCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((x) => x !== courseId) : [...prev, courseId]
    );
  };

  const saveStudentAssignments = async () => {
    if (!selectedStudentId) return alert("Select a student");
    await updateDoc(doc(db, "users", selectedStudentId), {
      courseIds: studentCourseIds,
      currentTermId: selectedTermId || null,
      updatedAt: serverTimestamp(),
    });
    await loadAll();
    alert("Student updated.");
  };

  const loadTimetable = async () => {
    setTimetableRows([]);
    if (!selectedTermId || !selectedCourseId) return;

    const snap = await getDocs(
        query(
        collection(db, "timetables"),
        where("termId", "==", selectedTermId),
        where("courseId", "==", selectedCourseId)
        )
    );

    const filtered = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort(
        (a, b) =>
            (a.dayOfWeek - b.dayOfWeek) ||
            (a.startTime || "").localeCompare(b.startTime || "")
        );

    setTimetableRows(filtered);
    };



  const addTimetableRow = async () => {
    if (!selectedTermId) return alert("Select a term");
    if (!selectedCourseId) return alert("Select a course");
    if (!ttStart || !ttEnd) return alert("Enter start/end time");

    await addDoc(collection(db, "timetables"), {
      termId: selectedTermId,
      courseId: selectedCourseId,
      dayOfWeek: Number(ttDay),
      startTime: ttStart,
      endTime: ttEnd,
      room: ttRoom.trim() || null,
      campus: ttCampus.trim() || null,
      lecturer: ttLecturer.trim() || null,
      createdAt: serverTimestamp(),
    });

    setTtRoom("");
    setTtCampus("");
    setTtLecturer("");

    await loadTimetable();
  };

  const deleteTimetableRow = async (id) => {
    if (!window.confirm("Delete this timetable entry?")) return;
    await deleteDoc(doc(db, "timetables", id));
    await loadTimetable();
  };

  // ---- Units: load per (term, course) ----
  const loadUnits = async () => {
    setExistingUnitsDocId(null);
    setTermUnits([]);

    if (!selectedTermId || !selectedCourseId) return;

    const snap = await getDocs(
      query(
        collection(db, "termUnits"),
        where("termId", "==", selectedTermId),
        where("courseId", "==", selectedCourseId)
      )
    );

    const first = snap.docs[0];
    if (!first) return;

    const data = { id: first.id, ...first.data() };
    setExistingUnitsDocId(data.id);
    setTermUnits(Array.isArray(data.units) ? data.units : []);
  };
  const ensureTermUnitsDoc = async () => {
    if (existingUnitsDocId) return existingUnitsDocId;

    const created = await addDoc(collection(db, "termUnits"), {
      termId: selectedTermId,
      courseId: selectedCourseId,
      units: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    setExistingUnitsDocId(created.id);
    return created.id;
  };
  const addUnit = async () => {
    if (!selectedTermId) return alert("Select a term");
    if (!selectedCourseId) return alert("Select a course");
    if (!unitTitle.trim()) return alert("Enter unit title");
    if (!unitStart || !unitEnd) return alert("Enter unit start/end dates");
    if (unitEnd < unitStart) return alert("End date must be after start date");

    setSavingUnits(true);
    try {
      const docId = await ensureTermUnitsDoc();

      const newUnit = {
        id: crypto.randomUUID(),
        title: unitTitle.trim(),
        startDate: unitStart,
        endDate: unitEnd,
      };

      const nextUnits = [...termUnits, newUnit].sort((a, b) =>
        (a.startDate || "").localeCompare(b.startDate || "")
      );

      await updateDoc(doc(db, "termUnits", docId), {
        units: nextUnits,
        updatedAt: serverTimestamp(),
      });

      setTermUnits(nextUnits);
      setUnitTitle("");
      setUnitStart("");
      setUnitEnd("");
    } finally {
      setSavingUnits(false);
    }
  };

  const deleteUnit = async (unitId) => {
    if (!existingUnitsDocId) return;
    if (!window.confirm("Delete this unit?")) return;

    setSavingUnits(true);
    try {
      const nextUnits = termUnits.filter((u) => u.id !== unitId);

      await updateDoc(doc(db, "termUnits", existingUnitsDocId), {
        units: nextUnits,
        updatedAt: serverTimestamp(),
      });

      setTermUnits(nextUnits);
    } finally {
      setSavingUnits(false);
    }
  };



  const saveUnits = async () => {
    if (!selectedTermId) return alert("Select a term");
    if (!selectedCourseId) return alert("Select a course");

    const units = unitLines
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    setSavingUnits(true);
    try {
      if (existingUnitsDocId) {
        await updateDoc(doc(db, "termUnits", existingUnitsDocId), {
          units,
          updatedAt: serverTimestamp(),
        });
      } else {
        const created = await addDoc(collection(db, "termUnits"), {
          termId: selectedTermId,
          courseId: selectedCourseId,
          units,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setExistingUnitsDocId(created.id);
      }
      alert("Units saved.");
    } finally {
      setSavingUnits(false);
    }
  };

  // ---------- UI ----------
  return (
    <div className="content-area admin-shell">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1 className="page-title">Student Panel</h1>
            <p className="page-subtitle">Manage students, courses, timetables and term units.</p>
            {loading && <div className="admin-muted">Refreshing…</div>}
          </div>

          <button className="btn btn-secondary" type="button" onClick={onBackClick}>
            ← Back
          </button>
        </div>
      </div>

      {/* Courses */}
      <div className="admin-card form-container">
        <div className="admin-card-header">
          <div>
            <h2 className="admin-card-title">📚 Courses</h2>
            <p className="admin-card-subtitle">Create and manage courses.</p>
          </div>
        </div>

        <div className="admin-surface">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Course name</label>
              <input className="form-input" value={courseName} onChange={(e) => setCourseName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Course code (optional)</label>
              <input className="form-input" value={courseCode} onChange={(e) => setCourseCode(e.target.value)} />
            </div>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" type="button" onClick={createCourse}>
              Add Course
            </button>
          </div>

          <div style={{ marginTop: 14 }}>
            {courses.length === 0 ? (
              <div className="admin-muted">No courses yet.</div>
            ) : (
              <div className="admin-folder-tree">
                {courses.map((c) => (
                  <div key={c.id} className="admin-folder-row">
                    <div className="admin-muted">
                      <b>{c.name}</b> {c.code ? `(${c.code})` : ""}
                    </div>
                    <button className="btn btn-secondary" type="button" onClick={() => deleteCourse(c.id)}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="admin-card form-container">
        <div className="admin-card-header">
          <div>
            <h2 className="admin-card-title">📅 Terms</h2>
            <p className="admin-card-subtitle">Create and manage terms.</p>
          </div>
        </div>

        <div className="admin-surface">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Term name</label>
              <input className="form-input" value={termName} onChange={(e) => setTermName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Start date (optional)</label>
              <input className="form-input" type="date" value={termStart} onChange={(e) => setTermStart(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">End date (optional)</label>
              <input className="form-input" type="date" value={termEnd} onChange={(e) => setTermEnd(e.target.value)} />
            </div>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" type="button" onClick={createTerm}>
              Add Term
            </button>
          </div>

          <div style={{ marginTop: 14 }}>
            {terms.length === 0 ? (
              <div className="admin-muted">No terms yet.</div>
            ) : (
              <div className="admin-folder-tree">
                {terms.map((t) => (
                  <div key={t.id} className="admin-folder-row">
                    <div className="admin-muted">
                      <b>{t.name}</b>{" "}
                      {t.startDate ? `• ${t.startDate}` : ""}{t.endDate ? ` → ${t.endDate}` : ""}
                    </div>
                    <button className="btn btn-secondary" type="button" onClick={() => deleteTerm(t.id)}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timetable + Units: pick term & course */}
      <div className="admin-card form-container">
        <div className="admin-card-header">
          <div>
            <h2 className="admin-card-title">🗓️ Timetable + Term Units</h2>
            <p className="admin-card-subtitle">Define sessions + units for each course in a term.</p>
          </div>
        </div>

        <div className="admin-surface">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Term</label>
              <select className="form-select" value={selectedTermId} onChange={(e) => setSelectedTermId(e.target.value)}>
                <option value="">Select…</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Course</label>
              <select className="form-select" value={selectedCourseId} onChange={(e) => setSelectedCourseId(e.target.value)}>
                <option value="">Select…</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>


          {/* Units per term/course */}
          <div style={{ marginTop: 18 }}>
            <h3 className="admin-subtitle">Units to be covered (this term)</h3>
            <div className="admin-muted" style={{ marginBottom: 8 }}>
              Add each unit with a title + start/end dates.
            </div>

            {/* Add unit form */}
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Unit title</label>
                <input
                  className="form-input"
                  value={unitTitle}
                  onChange={(e) => setUnitTitle(e.target.value)}
                  disabled={!selectedTermId || !selectedCourseId || savingUnits}
                  placeholder="Week 1: Introduction"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Start date</label>
                <input
                  className="form-input"
                  type="date"
                  value={unitStart}
                  onChange={(e) => setUnitStart(e.target.value)}
                  disabled={!selectedTermId || !selectedCourseId || savingUnits}
                />
              </div>

              <div className="form-group">
                <label className="form-label">End date</label>
                <input
                  className="form-input"
                  type="date"
                  value={unitEnd}
                  onChange={(e) => setUnitEnd(e.target.value)}
                  disabled={!selectedTermId || !selectedCourseId || savingUnits}
                />
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: 12 }}>
              <button
                className="btn btn-primary"
                type="button"
                onClick={addUnit}
                disabled={!selectedTermId || !selectedCourseId || savingUnits}
              >
                {savingUnits ? "Saving..." : "Add Unit"}
              </button>
            </div>

            {/* Units list */}
            {!selectedTermId || !selectedCourseId ? (
              <div className="admin-muted" style={{ marginTop: 10 }}>
                Select term and course to manage units.
              </div>
            ) : termUnits.length === 0 ? (
              <div className="admin-muted" style={{ marginTop: 10 }}>
                No units yet.
              </div>
            ) : (
              <div style={{ marginTop: 10 }} className="admin-folder-tree">
                {termUnits.map((u) => (
                  <div key={u.id} className="admin-folder-row">
                    <div className="admin-muted">
                      <b>{u.title}</b>
                      {" "}
                      {u.startDate ? `• ${u.startDate}` : ""}
                      {u.endDate ? ` → ${u.endDate}` : ""}
                    </div>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => deleteUnit(u.id)}
                      disabled={savingUnits}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}